import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
// استدعينا أيقونة الدرع للتربية العسكرية (FaShieldAlt)
import { FaCalendarAlt, FaKey, FaFutbol, FaTrophy, FaArrowLeft, FaRobot, FaBell, FaShieldAlt } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";

// --- Feature Section Component (Bilingual & Responsive) ---
const FeatureSection = ({ icon: Icon, titleAr, titleEn, descAr, descEn, align = "left", color }) => {
  return (
    <div className={`row align-items-center py-5 my-4 ${align === "right" ? "flex-md-row-reverse" : ""}`}>
      
      {/* Icon Side */}
      <div className="col-12 col-md-5 text-center mb-4 mb-md-0 d-flex justify-content-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          className="d-flex align-items-center justify-content-center rounded-circle shadow-lg position-relative"
          style={{ 
            width: "clamp(140px, 15vw, 180px)", 
            height: "clamp(140px, 15vw, 180px)", 
            background: `${color}15`, 
            border: `2px solid ${color}30` 
          }}
        >
            {/* Glow Effect */}
            <div className="position-absolute rounded-circle w-100 h-100" style={{ boxShadow: `0 0 40px ${color}40` }}></div>
            {/* Icon Size */}
            <div style={{ fontSize: "clamp(50px, 5vw, 70px)" }}>
                <Icon color={color} />
            </div>
        </motion.div>
      </div>

      {/* Text Side - Bilingual Layout */}
      <div className="col-12 col-md-7 px-4 px-md-3">
        <motion.div
           initial={{ y: 30, opacity: 0 }}
           whileInView={{ y: 0, opacity: 1 }}
           viewport={{ once: true }}
           transition={{ duration: 0.6 }}
           className="d-flex flex-column gap-3"
        >
            {/* Titles */}
            <div className="d-flex flex-column gap-1">
              <h2 dir="rtl" className="fw-bold text-white mb-0 text-center text-md-end" style={{ fontSize: "clamp(1.75rem, 2.5vw, 2.2rem)" }}>
                {titleAr}
              </h2>
              <h3 dir="ltr" className="mb-0 text-center text-md-start" style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)", color: color, opacity: 0.8 }}>
                {titleEn}
              </h3>
            </div>

            {/* Descriptions */}
            <div className="d-flex flex-column gap-2 mt-2">
              <p dir="rtl" className="text-light mb-0 text-center text-md-end" style={{ fontSize: "clamp(1.1rem, 1.3vw, 1.25rem)", lineHeight: "1.6" }}>
                {descAr}
              </p>
              <hr className="m-0 border-secondary opacity-25" />
              <p dir="ltr" className="text-secondary mb-0 text-center text-md-start" style={{ fontSize: "clamp(0.95rem, 1vw, 1.1rem)", lineHeight: "1.5" }}>
                {descEn}
              </p>
            </div>
        </motion.div>
      </div>
    </div>
  );
};

export default function About() {
  const navigate = useNavigate();

  return (
    <div style={{ background: "#09090b", minHeight: "100vh", overflowX: "hidden" }} className="text-white">
      
      {/* --- Navbar Fixed --- */}
      <nav className="position-fixed top-0 start-0 p-3 w-100 d-flex justify-content-start" style={{ zIndex: 100 }}>
        <button 
            onClick={() => navigate(-1)} 
            className="btn btn-dark btn-sm rounded-pill px-3 py-2 d-flex align-items-center gap-2 border border-secondary border-opacity-25 shadow-lg"
            style={{ backdropFilter: "blur(10px)", background: "rgba(0,0,0,0.5)" }}
        >
            <FaArrowLeft size={14} /> 
            <span className="small fw-bold">العودة | Back</span>
        </button>
      </nav>

      {/* --- Hero Section --- */}
      <header className="container d-flex flex-column align-items-center justify-content-center text-center min-vh-100 position-relative py-5">
         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="z-2 mt-5 mt-md-0 w-100"
         >
            <span className="text-info fw-bold letter-spacing-2 text-uppercase mb-4 d-block small">
              لماذا CampusPulse؟ | Why CampusPulse?
            </span>
            
            {/* Title Bilingual */}
            <div className="mb-4">
              <h1 className="fw-bolder text-white mb-2" dir="rtl" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", lineHeight: "1.2" }}>
                أكثر من مجرد <span className="text-primary">منصة للفعاليات</span>.
              </h1>
              <h2 className="fw-bolder text-white-50" dir="ltr" style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}>
                  More Than Just An <span className="text-info position-relative" style={{ display: "inline-block" }}>
                      Events Board
                      <svg className="position-absolute start-0 w-100" style={{ bottom: "-8px", height: "8px" }} viewBox="0 0 100 10" preserveAspectRatio="none">
                          <path d="M0 5 Q 50 10 100 5" stroke="#0ea5e9" strokeWidth="3" fill="transparent" opacity="0.6" />
                      </svg>
                  </span>.
              </h2>
            </div>
            
            {/* Description Bilingual */}
            <div className="mx-auto d-flex flex-column gap-3" style={{ maxWidth: "700px" }}>
              <p className="lead text-light mb-0 px-3" dir="rtl" style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.25rem)", lineHeight: "1.6" }}>
                  بيئة متكاملة مصممة لربط الطلاب بالحياة الجامعية. اختبر الجيل القادم من إدارة الحرم الجامعي بكل سهولة وذكاء.
              </p>
              <p className="text-secondary px-3" dir="ltr" style={{ fontSize: "clamp(0.95rem, 1.2vw, 1.1rem)" }}>
                  A complete ecosystem designed to bridge the gap between students and university life. Experience the next generation of campus management.
              </p>
            </div>
         </motion.div>
         
         {/* Scroll Indicator */}
         <motion.div 
            animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="position-absolute bottom-0 mb-3 mb-md-5 text-secondary"
         >
            <span className="small d-block mb-1" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>استكشف المزيد</span>
            <span className="small d-block mb-2" style={{ letterSpacing: '2px', fontSize: '0.75rem', opacity: 0.6 }}>SCROLL TO EXPLORE</span>
            <div className="d-block mx-auto border border-secondary rounded-pill" style={{ width: "20px", height: "35px" }}>
                <div className="bg-secondary rounded-circle mx-auto mt-1" style={{ width: "4px", height: "4px" }}></div>
            </div>
         </motion.div>
      </header>

      {/* --- Features Journey (مرتبة تسويقياً ومنطقياً) --- */}
      <div className="container py-3 py-md-5 overflow-hidden">
         
         {/* 1. Discovery */}
         <FeatureSection 
            icon={FaCalendarAlt} 
            color="#3b82f6" 
            titleAr="اكتشاف ذكي للفعاليات"
            titleEn="Smart Event Discovery" 
            descAr="استكشف الحياة الجامعية بذكاء. ابحث عن ورش العمل، الندوات، والأنشطة الاجتماعية المناسبة لاهتماماتك."
            descEn="Explore university life like never before. Filter workshops, seminars, and social activities tailored to your faculty and interests."
         />

         {/* 2. Sports */}
         <FeatureSection 
            icon={FaFutbol} 
            color="#10b981" 
            align="right"
            titleAr="حجز الملاعب الرياضية"
            titleEn="Sports Arena Booking" 
            descAr="مستعد للعب؟ احجز ملاعب كرة القدم ⚽، السلة 🏀، أو الطائرة 🏐 بكل سهولة وفي ثوانٍ معدودة."
            descEn="Ready to play? Book courts for Football ⚽, Basketball 🏀, or Volleyball 🏐 effortlessly. Reserve your team's spot in seconds."
         />

         {/* 3. Military Education (الميزة الجديدة الجامدة) */}
         <FeatureSection 
            icon={FaShieldAlt} 
            color="#14b8a6" 
            titleAr="تسجيل التربية العسكرية بضغطة زر"
            titleEn="Hassle-Free Military Education" 
            descAr="وداعاً للروتين الورقي! وفرنا نظاماً ذكياً يسهل على الطلاب التسجيل في دورات التربية العسكرية، ويوفر للإدارة لوحة تحكم كاملة لإدارة الطلبات بكفاءة."
            descEn="Say goodbye to paperwork. Our smart system simplifies the Military Education registration process for students and gives admins a powerful dashboard to manage applications efficiently."
         />

         {/* 4. AI Guide */}
         <FeatureSection 
            icon={FaRobot} 
            color="#06b6d4" 
            align="right"
            titleAr="دليلك الشخصي الذكي"
            titleEn="Your Personal AI Guide" 
            descAr="تائه؟ الشات بوت الذكي الخاص بنا متاح على مدار الساعة للإجابة على أسئلتك، تقديم اقتراحات، وتوجيهك فوراً."
            descEn="Lost? Our intelligent Chatbot is here to help 24/7. Ask for recommendations, event details, or guidance, and get instant smart answers."
         />

         {/* 5. OTP */}
         <FeatureSection 
            icon={FaKey} 
            color="#8b5cf6" 
            titleAr="تسجيل حضور آمن بالـ OTP"
            titleEn="Secure OTP Check-in" 
            descAr="أثبت حضورك فوراً باستخدام رمز أمان مؤقت (OTP) مخصص لكل فعالية. نظام سريع، موثوق، وآمن تماماً."
            descEn="Verify your attendance instantly using a secure One-Time Password (OTP) generated for every event. Fast, reliable, and cheat-proof."
         />

         {/* 6. Notifications */}
         <FeatureSection 
            icon={FaBell} 
            color="#f43f5e" 
            align="right"
            titleAr="إشعارات وتنبيهات فورية"
            titleEn="Stay Connected & Informed" 
            descAr="خليك دايماً في الصورة. استقبل إشعارات فورية ورسائل بريد إلكتروني عن الفعاليات الجديدة وتأكيدات الحجز."
            descEn="Never miss a beat. Get instant In-App notifications and Email alerts for new events, booking confirmations, or schedule updates."
         />

         {/* 7. Leaderboard */}
         <FeatureSection 
            icon={FaTrophy} 
            color="#f59e0b" 
            titleAr="تصدر لوحة الشرف"
            titleEn="Rise to the Top" 
            descAr="حول مشاركتك لتحدي! اجمع نقاط من كل نشاط ونافس للوصول لقائمة أفضل 5 طلاب على مستوى الجامعة."
            descEn="Turn participation into a game! Earn points for every activity. Compete to reach the 'Top 5' Leaderboard on the main dashboard."
         />

      </div>

      {/* --- CTA Footer --- */}
      <footer className="py-5 mt-3 text-center position-relative overflow-hidden">
        <div className="position-absolute w-100 h-100 top-0 start-0 bg-primary opacity-10" 
             style={{ filter: "blur(80px)", zIndex: 0 }}></div>
        <div className="container position-relative z-1 px-3">
            <h2 className="fw-bold text-white mb-2" dir="rtl" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}>
              مستعد للانضمام للمنصة؟
            </h2>
            <h3 className="text-secondary mb-4" dir="ltr" style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)" }}>
              Ready to join the hype?
            </h3>
            <button 
                onClick={() => navigate('/auth/register')}
                className="btn btn-light btn-lg px-5 py-3 rounded-pill shadow-lg border-0 d-inline-flex flex-column align-items-center gap-1"
                style={{ transition: "all 0.3s ease" }}
            >
                <span className="fw-bold text-dark" style={{ fontSize: "1.1rem" }}>ابدأ الآن 🚀</span>
                <span className="text-muted" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Launch App</span>
            </button>
        </div>
      </footer>

    </div>
  );
}

// import React from "react";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// // استدعينا الأيقونات الجديدة (روبوت، جرس، وكدة)
// import { FaCalendarAlt, FaKey, FaFutbol, FaTrophy, FaArrowLeft, FaRobot, FaBell } from "react-icons/fa";
// import "bootstrap/dist/css/bootstrap.min.css";

// // --- Feature Section Component (Fixed & Responsive) ---
// const FeatureSection = ({ icon: Icon, title, desc, align = "left", color }) => {
//   return (
//     <div className={`row align-items-center py-5 my-4 ${align === "right" ? "flex-md-row-reverse" : ""}`}>
      
//       {/* Icon Side */}
//       <div className="col-12 col-md-6 text-center mb-4 mb-md-0 d-flex justify-content-center">
//         <motion.div
//           initial={{ scale: 0.8, opacity: 0 }}
//           whileInView={{ scale: 1, opacity: 1 }}
//           viewport={{ once: true, margin: "-50px" }}
//           className="d-flex align-items-center justify-content-center rounded-circle shadow-lg position-relative"
//           style={{ 
//             width: "clamp(140px, 15vw, 180px)", 
//             height: "clamp(140px, 15vw, 180px)", 
//             background: `${color}15`, 
//             border: `2px solid ${color}30` 
//           }}
//         >
//             {/* Glow Effect */}
//             <div className="position-absolute rounded-circle w-100 h-100" style={{ boxShadow: `0 0 40px ${color}40` }}></div>
//             {/* Icon Size */}
//             <div style={{ fontSize: "clamp(50px, 5vw, 70px)" }}>
//                 <Icon color={color} />
//             </div>
//         </motion.div>
//       </div>

//       {/* Text Side */}
//       <div className="col-12 col-md-6 text-center text-md-start px-4 px-md-3">
//         <motion.div
//            initial={{ y: 30, opacity: 0 }}
//            whileInView={{ y: 0, opacity: 1 }}
//            viewport={{ once: true }}
//            transition={{ duration: 0.6 }}
//         >
//             <h2 className="fw-bold text-white mb-3" style={{ fontSize: "clamp(1.75rem, 2.5vw, 2.5rem)" }}>{title}</h2>
//             <p className="text-secondary lead" style={{ fontSize: "clamp(1rem, 1.2vw, 1.25rem)" }}>{desc}</p>
//         </motion.div>
//       </div>
//     </div>
//   );
// };

// export default function About() {
//   const navigate = useNavigate();

//   return (
//     <div style={{ background: "#09090b", minHeight: "100vh", overflowX: "hidden" }} className="text-white">
      
//       {/* --- Navbar Fixed --- */}
//       <nav className="position-fixed top-0 start-0 p-3 w-100 d-flex justify-content-start" style={{ zIndex: 100 }}>
//         <button 
//             onClick={() => navigate('/')} 
//             className="btn btn-dark btn-sm rounded-pill px-3 py-2 d-flex align-items-center gap-2 border border-secondary border-opacity-25 shadow-lg"
//             style={{ backdropFilter: "blur(10px)", background: "rgba(0,0,0,0.5)" }}
//         >
//             <FaArrowLeft size={14} /> <span className="small fw-bold">Back</span>
//         </button>
//       </nav>

//       {/* --- Hero Section --- */}
//       <header className="container d-flex flex-column align-items-center justify-content-center text-center min-vh-100 position-relative py-5">
//          <motion.div
//             initial={{ opacity: 0, y: 30 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 1 }}
//             className="z-2 mt-5 mt-md-0"
//          >
//             <span className="text-info fw-bold letter-spacing-2 text-uppercase mb-3 d-block small">Why CampusPulse?</span>
            
//             <h1 className="fw-bolder mb-4 text-white" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", lineHeight: "1.1" }}>
//                 More Than Just <br/> An <span className="text-primary position-relative" style={{ display: "inline-block" }}>
//                     Events Board
//                     <svg className="position-absolute start-0 w-100" style={{ bottom: "-10px", height: "10px" }} viewBox="0 0 100 10" preserveAspectRatio="none">
//                         <path d="M0 5 Q 50 10 100 5" stroke="#0ea5e9" strokeWidth="3" fill="transparent" opacity="0.6" />
//                     </svg>
//                 </span>.
//             </h1>
            
//             <p className="lead text-secondary mx-auto px-3" style={{ maxWidth: "600px", fontSize: "clamp(1rem, 1.5vw, 1.25rem)" }}>
//                 A complete ecosystem designed to bridge the gap between students and university life. 
//                 Experience the next generation of campus management.
//             </p>
//          </motion.div>
         
//          {/* Scroll Indicator */}
//          <motion.div 
//             animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
//             transition={{ repeat: Infinity, duration: 2 }}
//             className="position-absolute bottom-0 mb-3 mb-md-5 text-secondary"
//          >
//             <span className="small d-block mb-2" style={{ letterSpacing: '1px', fontSize: '0.8rem' }}>SCROLL TO EXPLORE</span>
//             <div className="d-block mx-auto border border-secondary rounded-pill" style={{ width: "20px", height: "35px" }}>
//                 <div className="bg-secondary rounded-circle mx-auto mt-1" style={{ width: "4px", height: "4px" }}></div>
//             </div>
//          </motion.div>
//       </header>

//       {/* --- Features Journey --- */}
//       <div className="container py-3 py-md-5">
         
//          {/* 1. Smart Discovery */}
//          <FeatureSection 
//             icon={FaCalendarAlt} 
//             color="#3b82f6" // Sky Blue
//             title="Smart Event Discovery" 
//             desc="Explore university life like never before. Filter workshops, seminars, and social activities tailored to your faculty and interests."
//          />

//          {/* 2. AI Guide (NEW) */}
//          <FeatureSection 
//             icon={FaRobot} 
//             color="#06b6d4" // Cyan
//             align="right"
//             title="Your Personal AI Guide" 
//             desc="Lost? Our intelligent Chatbot is here to help 24/7. Ask for recommendations, event details, or guidance, and get instant smart answers."
//          />

//          {/* 3. Sports Booking */}
//          <FeatureSection 
//             icon={FaFutbol} 
//             color="#10b981" // Emerald
//             title="Sports Arena Booking" 
//             desc="Ready to play? Book courts for Football ⚽, Basketball 🏀, or Volleyball 🏐 effortlessly. Reserve your team's spot in seconds."
//          />

//          {/* 4. Notifications (NEW) */}
//          <FeatureSection 
//             icon={FaBell} 
//             color="#f43f5e" // Rose
//             align="right"
//             title="Stay Connected & Informed" 
//             desc="Never miss a beat. Get instant In-App notifications and Email alerts for new events, booking confirmations, or schedule updates."
//          />

//          {/* 5. OTP Check-in */}
//          <FeatureSection 
//             icon={FaKey} 
//             color="#8b5cf6" // Violet
//             title="Secure OTP Check-in" 
//             desc="Verify your attendance instantly using a secure One-Time Password (OTP) generated for every event. Fast, reliable, and cheat-proof."
//          />

//          {/* 6. Leaderboard */}
//          <FeatureSection 
//             icon={FaTrophy} 
//             color="#f59e0b" // Gold
//             align="right"
//             title="Rise to the Top" 
//             desc="Turn participation into a game! Earn points for every activity. Compete to reach the 'Top 5' Leaderboard on the main dashboard."
//          />

//       </div>

//       {/* --- CTA Footer --- */}
//       <footer className="py-5 mt-3 text-center position-relative overflow-hidden">
//         <div className="position-absolute w-100 h-100 top-0 start-0 bg-primary opacity-10" 
//              style={{ filter: "blur(80px)", zIndex: 0 }}></div>
//         <div className="container position-relative z-1 px-3">
//             <h2 className="fw-bold mb-4" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}>Ready to join the hype?</h2>
//             <button 
//                 onClick={() => navigate('/')}
//                 className="btn btn-light btn-lg px-5 py-3 rounded-pill fw-bold shadow-lg border-0"
//                 style={{ fontSize: "1.1rem" }}
//             >
//                 Launch App 🚀
//             </button>
//         </div>
//       </footer>

//     </div>
//   );
// }
